import React, { useState } from 'react'
import { useBrickStore } from '@store/brickStore'
import { generateRandomBricks } from '@utils/randomBricks'
import { isDebugMode, isPhysicsDisabled } from '@utils/debug'
import { globalProfiler, isProfilingEnabled } from '@utils/performanceProfiling'

/**
 * DebugPanel Component
 *
 * Debug mode tools for performance testing and bulk brick generation.
 * Only visible when URL contains ?debug=true query parameter.
 *
 * Features:
 * - Bulk brick generation: 100, 1000, 10000 bricks
 * - Random brick types: 1x1, 1x2, 2x2, 2x4, 1x4
 * - Random colors: 6 preset LEGO colors
 * - Random rotations: 0°, 90°, 180°, 270°
 * - Random positions within bounds
 * - Clear all bricks
 * - Real-time FPS counter (updated from parent)
 * - Total brick count display
 *
 * Performance Considerations:
 * - Bypasses collision detection for bulk generation (speed)
 * - Generates bricks directly into store
 * - Uses optimized random generation utilities
 *
 * Task: 4.1.0 - Debug mode with bulk brick testing tools
 *
 * @component
 */
export const DebugPanel: React.FC<{ fps: number }> = ({ fps }) => {
  const bricks = useBrickStore(state => state.bricks)
  const addBrick = useBrickStore(state => state.addBrick)
  const clearScene = useBrickStore(state => state.clearScene)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isProfiling, setIsProfiling] = useState(globalProfiler.isProfilingActive())
  const physicsDisabled = isPhysicsDisabled()
  const profilingEnabled = isProfilingEnabled()

  // Don't render if debug mode is not enabled
  if (!isDebugMode()) {
    return null
  }

  const handleGenerateBricks = async (count: number) => {
    setIsGenerating(true)

    // Use setTimeout to allow UI to update before heavy operation
    setTimeout(() => {
      const startTime = performance.now()

      // Generate random bricks
      const randomBricks = generateRandomBricks(count)

      // Add all bricks to store (bypassing collision detection)
      randomBricks.forEach(brick => addBrick(brick))

      const endTime = performance.now()
      const duration = endTime - startTime

      console.log(`[Debug] Generated ${count} bricks in ${duration.toFixed(2)}ms`)

      setIsGenerating(false)
    }, 100)
  }

  const handleClearAll = () => {
    if (window.confirm(`Clear all ${bricks.size} bricks?`)) {
      clearScene()
      console.log('[Debug] Scene cleared')
    }
  }

  const handleStartProfiling = () => {
    globalProfiler.startProfiling(!physicsDisabled)
    setIsProfiling(true)
  }

  const handleStopProfiling = () => {
    globalProfiler.stopProfiling()
    const report = globalProfiler.generateReport()
    globalProfiler.printReport(report)

    // Download report as JSON
    const json = globalProfiler.exportReportJSON(report)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `perf-report-${report.brickCount}bricks-${report.physicsEnabled ? 'physics' : 'nophysics'}-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    setIsProfiling(false)
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        border: '2px solid #ff0066',
        borderRadius: '8px',
        padding: '16px',
        color: '#ffffff',
        fontFamily: 'monospace',
        fontSize: '12px',
        minWidth: '200px',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(255, 0, 102, 0.3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          marginBottom: '12px',
          color: '#ff0066',
          borderBottom: '1px solid #ff0066',
          paddingBottom: '8px',
        }}
      >
        DEBUG MODE
      </div>

      {/* Stats */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ color: '#888' }}>FPS:</span>{' '}
          <span style={{ color: fps < 30 ? '#ff4444' : fps < 50 ? '#ffaa00' : '#44ff44' }}>
            {fps}
          </span>
        </div>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ color: '#888' }}>Bricks:</span>{' '}
          <span style={{ color: '#00ccff' }}>{bricks.size}</span>
        </div>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ color: '#888' }}>Physics:</span>{' '}
          <span style={{ color: physicsDisabled ? '#ff4444' : '#44ff44' }}>
            {physicsDisabled ? 'OFF' : 'ON'}
          </span>
        </div>
        {profilingEnabled && (
          <div>
            <span style={{ color: '#888' }}>Profiling:</span>{' '}
            <span style={{ color: isProfiling ? '#44ff44' : '#888' }}>
              {isProfiling ? 'ACTIVE' : 'IDLE'}
            </span>
          </div>
        )}
      </div>

      {/* Bulk Generation Buttons */}
      <div style={{ marginBottom: '12px' }}>
        <div
          style={{
            fontSize: '11px',
            color: '#888',
            marginBottom: '6px',
          }}
        >
          BULK GENERATION
        </div>

        <button
          onClick={() => handleGenerateBricks(100)}
          disabled={isGenerating}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '6px',
            backgroundColor: isGenerating ? '#333' : '#0066cc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontFamily: 'monospace',
            opacity: isGenerating ? 0.5 : 1,
          }}
        >
          Add 100 Bricks
        </button>

        <button
          onClick={() => handleGenerateBricks(1000)}
          disabled={isGenerating}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '6px',
            backgroundColor: isGenerating ? '#333' : '#0066cc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontFamily: 'monospace',
            opacity: isGenerating ? 0.5 : 1,
          }}
        >
          Add 1,000 Bricks
        </button>

        <button
          onClick={() => handleGenerateBricks(10000)}
          disabled={isGenerating}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '6px',
            backgroundColor: isGenerating ? '#333' : '#ff6600',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontFamily: 'monospace',
            opacity: isGenerating ? 0.5 : 1,
          }}
        >
          Add 10,000 Bricks
        </button>
      </div>

      {/* Clear All Button */}
      <button
        onClick={handleClearAll}
        disabled={isGenerating || bricks.size === 0}
        style={{
          width: '100%',
          padding: '8px',
          marginBottom: '12px',
          backgroundColor: isGenerating || bricks.size === 0 ? '#333' : '#cc0000',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: isGenerating || bricks.size === 0 ? 'not-allowed' : 'pointer',
          fontSize: '12px',
          fontFamily: 'monospace',
          opacity: isGenerating || bricks.size === 0 ? 0.5 : 1,
        }}
      >
        Clear All
      </button>

      {/* Performance Profiling Controls (Task 4.1.6) */}
      {profilingEnabled && (
        <>
          <div
            style={{
              fontSize: '11px',
              color: '#888',
              marginBottom: '6px',
              borderTop: '1px solid #444',
              paddingTop: '12px',
            }}
          >
            PERFORMANCE PROFILING
          </div>

          {!isProfiling ? (
            <button
              onClick={handleStartProfiling}
              disabled={isGenerating}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: isGenerating ? '#333' : '#00cc66',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontFamily: 'monospace',
                opacity: isGenerating ? 0.5 : 1,
              }}
            >
              Start Profiling
            </button>
          ) : (
            <button
              onClick={handleStopProfiling}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#ff6600',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}
            >
              Stop & Export Report
            </button>
          )}
        </>
      )}

      {/* Generation Status */}
      {isGenerating && (
        <div
          style={{
            marginTop: '12px',
            padding: '8px',
            backgroundColor: 'rgba(255, 170, 0, 0.2)',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#ffaa00',
            textAlign: 'center',
          }}
        >
          Generating...
        </div>
      )}
    </div>
  )
}
